import cn from 'classnames'
import CheckmarkCircleIcon from 'ext-ui/svg/icon/CheckmarkCircleIcon'
import XCircleIcon from 'ext-ui/svg/icon/XCircleIcon'
import React, { ReactElement, useMemo } from 'react'
import { TranslateFn } from 'src/localization/translate'
import {
  PricingPlan,
  PricingPlanEntitlement,
} from 'src/queries/organizations/pricingPlans/pricingPlans.types'

import style from './PricingFeaturesComparisonTable.style'

interface PlanValue {
  displayName: string
}

interface EntitlementWithPlans {
  key: string
  displayName: string
  displayOrder: number
  featureName?: string
  hasVaryingDisplayNames: boolean
  planValues: Map<string, PlanValue> // planId -> plan-specific values
}

export interface PricingFeaturesComparisonTableProps {
  pricingPlans: PricingPlan[]
  className?: string
  translate: TranslateFn
}

export default function PricingFeaturesComparisonTable(
  props: PricingFeaturesComparisonTableProps,
): ReactElement {
  const { pricingPlans, className, translate } = props

  // Build a flat list of entitlements, each tracking which plans it belongs to.
  // Lower-tier entitlements cascade into upper tiers.
  const entitlements = useMemo(() => {
    const entitlementMap = new Map<string, EntitlementWithPlans>()
    let allPreviousEntitlements: PricingPlanEntitlement[] = []

    for (const plan of pricingPlans) {
      const planFullEntitlements = [
        ...plan.entitlements,
        ...allPreviousEntitlements,
      ]

      for (const entitlement of planFullEntitlements) {
        if (!entitlementMap.has(entitlement.key)) {
          entitlementMap.set(entitlement.key, {
            key: entitlement.key,
            displayName: entitlement.displayName,
            displayOrder: entitlement.displayOrder,
            featureName: entitlement.featureName,
            hasVaryingDisplayNames: false,
            planValues: new Map(),
          })
        }
        const entry = entitlementMap.get(entitlement.key)!
        if (entitlement.displayName !== entry.displayName) {
          entry.hasVaryingDisplayNames = true
        }
        if (!entry.planValues.has(plan.id)) {
          entry.planValues.set(plan.id, {
            displayName: entitlement.displayName,
          })
        }
      }

      allPreviousEntitlements = planFullEntitlements
    }

    return Array.from(entitlementMap.values()).sort(
      (a, b) => a.displayOrder - b.displayOrder,
    )
  }, [pricingPlans])

  if (entitlements.length === 0) {
    return <></>
  }

  return (
    <div
      className={cn('PricingFeaturesComparisonTable', className)}
      css={style}
    >
      <table>
        <thead>
          <tr>
            <th scope="col">{translate('Features')}</th>
            {pricingPlans.map((plan) => (
              <th key={plan.id} scope="col">
                {plan.displayName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entitlements.map((entitlement) => (
            <tr key={entitlement.key}>
              <td>
                {entitlement.hasVaryingDisplayNames
                  ? (entitlement.featureName ?? entitlement.displayName)
                  : entitlement.displayName}
              </td>
              {pricingPlans.map((plan) => {
                const planValue = entitlement.planValues.get(plan.id)

                if (!planValue) {
                  return (
                    <td key={plan.id}>
                      <div className="ComparisonTable-icon">
                        <XCircleIcon aria-label={translate('Not included')} />
                      </div>
                    </td>
                  )
                }

                if (entitlement.hasVaryingDisplayNames) {
                  return <td key={plan.id}>{planValue.displayName}</td>
                }

                return (
                  <td key={plan.id}>
                    <div className="ComparisonTable-icon">
                      <CheckmarkCircleIcon aria-label={translate('Included')} />
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
